import type { ButtonBase, SvgIconTypeMap } from "@mui/material";
import {
	Fade,
	ListItemIcon,
	ListItemText,
	MenuItem,
	styled,
} from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import { hoverStyles } from "helpers/hoverStyles";
import { useState } from "react";
import { Link } from "react-router-dom";

const PREFIX = "NavBarAccountMenuItem";

const classes = {
	icon: `${PREFIX}-icon`,
	iconWrapper: `${PREFIX}-iconWrapper`,
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	color: "white",
	...hoverStyles(theme),

	[`& .${classes.iconWrapper}`]: {
		margin: "0.25rem 0",
		display: "grid",
	},

	[`& .${classes.icon}`]: {
		gridRow: 1,
		gridColumn: 1,
		color: "white",
	},
})) as typeof ButtonBase;

const StyledLink = styled(Link)(() => ({
	textDecoration: "none",
	display: "flex",
	color: "inherit",
	alignItems: "center",
}));

type Icon = OverridableComponent<SvgIconTypeMap<{}, "svg">>;

export interface NavBarAccountMenuItemProps {
	onClick?: () => void;
	href?: string;
	icons: {
		filled: Icon;
		outlined: Icon;
	};
	children: string;
}

export const NavBarAccountMenuItem = ({
	onClick,
	href,
	icons,
	children,
}: NavBarAccountMenuItemProps): JSX.Element => {
	const [isHover, setHover] = useState(false);
	const onMouseEnter = (): void => setHover(true);
	const onMouseLeave = (): void => setHover(false);

	let content = (
		<>
			<ListItemIcon>
				<div className={classes.iconWrapper}>
					<Fade in={isHover} timeout={750}>
						<icons.filled className={classes.icon} />
					</Fade>
					<Fade in={!isHover} timeout={750}>
						<icons.outlined className={classes.icon} />
					</Fade>
				</div>
			</ListItemIcon>
			<ListItemText>{children}</ListItemText>
		</>
	);

	if (href) {
		content = (
			<StyledLink to={href} onClick={onClick}>
				{content}
			</StyledLink>
		);
	}

	return (
		<StyledMenuItem
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onClick={onClick}
		>
			{content}
		</StyledMenuItem>
	);
};
